import React, { memo } from 'react';
import { useTranslation } from 'react-i18next';

import styled from 'styled-components';
import { Colors, IResumeDate, IResumeFields, Languages } from 'context';
import fontService, { FontWeight } from 'services/FontService';
import { desktop, tablet } from 'services/ui';
import { removeLabel } from 'services/removeLabel';

import { CheckBox, Icon, IconNames, Input, TextArea } from 'atoms';
import { InputLabel } from 'atoms/Input/Input';
import { InputYearMonth } from 'molecules';

/**
 * A single work-experience entry (spec 0018, increment B).
 *
 * Reused from the old resume card but repointed at the new career profile model:
 * pet-projects moved to their own top-level section, so this card no longer
 * carries projects. It gains a free-text `description` and an `isCurrent` flag
 * ("I work here now") that disables/clears the end date.
 */
export interface IExperienceValues {
  id: string;
  company: string;
  position: string;
  description: string;
  startDate: IResumeDate;
  endDate: IResumeDate;
  isCurrent: boolean;
}

export interface IResumeExperienceCard {
  language: Languages;
  fields: IResumeFields;
  index: number;
  values: IExperienceValues;
  onInputChange: (name: string, value: string | IResumeDate | boolean) => void;
  isEndDateDisabled: boolean;
  onToggleCurrent: () => void;
  onRemoveExperience: (id: string) => void;
}

export const ResumeExperienceCardComponent = memo(
  ({
    language,
    fields,
    index,
    values,
    onInputChange,
    isEndDateDisabled,
    onToggleCurrent,
    onRemoveExperience,
  }: IResumeExperienceCard) => {
    const { t } = useTranslation();
    return (
      <Wrapper>
        <RemoveButton
          type="button"
          aria-label={removeLabel(t, 'careerProfile.remove.experience', values.company)}
          onClick={() => onRemoveExperience(values.id)}>
          <StyledCloseIcon name={IconNames.CloseSm} />
        </RemoveButton>

        <Content>
          <StyledInput
            type="text"
            label={fields.experience.company.companyName.label}
            placeholder={fields.experience.company.companyName.placeholder}
            value={values.company}
            max={150}
            onChange={({ target: { value } }) =>
              onInputChange(`experience[${index}].company`, value)
            }
          />

          <ContentPart>
            <StyledInput
              type="text"
              label={fields.experience.company.position.label}
              placeholder={fields.experience.company.position.placeholder}
              value={values.position}
              max={100}
              onChange={({ target: { value } }) =>
                onInputChange(`experience[${index}].position`, value)
              }
            />

            <DatesWrapper>
              <InputWrapper>
                <Label>{fields.date.startLabel}</Label>
                <InputYearMonth
                  value={values.startDate}
                  language={language}
                  monthsPlaceholder={fields.date.month}
                  yearPlaceholder={fields.date.year}
                  isDisabled={false}
                  onSelect={(value) =>
                    onInputChange(`experience[${index}].startDate`, { ...value })
                  }
                />
              </InputWrapper>

              <InputWrapper>
                <Label>{fields.date.endLabel}</Label>
                <InputYearMonth
                  value={values.endDate}
                  language={language}
                  monthsPlaceholder={fields.date.month}
                  yearPlaceholder={fields.date.year}
                  isDisabled={isEndDateDisabled}
                  onSelect={(value) => onInputChange(`experience[${index}].endDate`, { ...value })}
                />
              </InputWrapper>
            </DatesWrapper>
          </ContentPart>

          <StyledCheckBox
            checked={values.isCurrent}
            text={fields.experience.isCurrent}
            onClick={onToggleCurrent}
          />

          <Label>{fields.experience.description.label}</Label>
          <StyledTextArea
            value={values.description}
            rows={4}
            maxLength={1500}
            placeholder={fields.experience.description.placeholder}
            onChange={({ target: { value } }) =>
              onInputChange(`experience[${index}].description`, value)
            }
          />
        </Content>
      </Wrapper>
    );
  },
);

const Wrapper = styled.div`
  position: relative;
  background-color: ${Colors.bg_gray};
  border-radius: 8px;
  padding: 20px 20px 28px;

  ${tablet} {
    padding: 24px;
  }

  ${desktop} {
    padding: 32px;
  }
`;

const RemoveButton = styled.button`
  cursor: pointer;
  border-radius: 50%;
  border: none;
  background-color: ${Colors.white};
  width: 32px;
  height: 32px;
  display: flex;
  justify-content: center;
  align-items: center;
  box-shadow: 0 4px 16px rgba(100, 100, 100, 0.2);
  position: absolute;
  top: -15px;
  right: -14px;
  z-index: 3;
  padding: 0;

  ${desktop} {
    width: 40px;
    height: 40px;
    top: -12px;
    right: -20px;
  }

  &:hover {
    & svg > * {
      transition: 0.5s;
      fill: ${Colors.gray_dark};
    }
  }
`;

const StyledCloseIcon = styled(Icon)`
  width: 20px;
  height: 20px;

  ${desktop} {
    width: 24px;
    height: 24px;
  }
`;

const Content = styled.div``;

const StyledInput = styled(Input)`
  ${InputLabel} {
    font-weight: ${FontWeight.medium};
  }
  width: 100%;
  margin-bottom: 24px;
`;

const ContentPart = styled.div`
  display: flex;
  flex-direction: column;

  ${desktop} {
    flex-direction: row;
    justify-content: space-between;
    gap: 24px;
  }
`;

const DatesWrapper = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 24px;
  margin-bottom: 24px;

  ${tablet} {
    flex-direction: row;
    justify-content: space-between;
  }

  ${desktop} {
    width: 50%;
  }
`;

const InputWrapper = styled.div`
  flex-grow: 1;
`;

const Label = styled.p`
  color: ${Colors.black};
  background-color: transparent;
  ${fontService.text({ size: 14, lineHeight: 125, weight: FontWeight.medium })};
  margin: 0 0 4px 0;
`;

const StyledCheckBox = styled(CheckBox)`
  margin-bottom: 24px;
`;

const StyledTextArea = styled(TextArea)`
  padding: 14px 12px;
  border-radius: 8px;
  ${fontService.text({ size: 16, lineHeight: 125, weight: FontWeight.regular })}
`;
